import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRightLeft, DatabaseZap, Globe, Loader2, Lock, Plus, RefreshCw, Share2, TriangleAlert } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { getGameById } from '@/config/games';
import { useActiveGameGroup, useGameGroupsStore } from '@/stores/gameGroups.store';
import { useWorldsStore } from './worlds.store';
import { followersOf, isFollower, leaderGroup, withGameGroup, worldsMissingOn } from './sharedMap';
import { POLL_MS, fmtInt, loadPref, savePref } from './constants';
import { DEFAULT_TAB, TAB_STORAGE_KEY, WORLDS_TABS, isWorldsTab, type WorldsTabKey } from './worlds.tabs';
import { useWorldsStats } from './useWorldsStats';
import { ImportPanel } from './components/ImportPanel';
import { Modal } from './components/Modal';
import { ConfirmHost } from './components/ConfirmDialog';
import { EmptyState, InfoBox } from './components/ui';
import { HomeTab } from './tabs/HomeTab';
import { MapTab } from './tabs/MapTab';
import { CountriesTab } from './tabs/CountriesTab';
import { GeoRegionsTab } from './tabs/GeoRegionsTab';
import { LevelsTab } from './tabs/LevelsTab';
import { MarkersTab } from './tabs/MarkersTab';
import styles from '../LootboxPage.module.scss';
import ws from './WorldsPage.module.scss';

// ============================================================
// Mondes (NationsGlory) : accueil, carte des pays, géo-régions, niveaux, marqueurs de biomes
// ============================================================
// Portage de Configurations > Worlds de NGConfigAPI, servi par le module « worlds » du satellite
// du projet actif (Mongo du satellite). Module « worlds » de Paramètres généraux > Fonctionnalités,
// actif par défaut sur NationsGlory seulement : là où il est coupé, le garde de route d'AdminLayout
// ne monte pas la page, donc aucun appel.
// Lecture : <projet>.config.worlds:read ; écriture (peinture, pays, import, synchro) :
// <projet>.config.worlds. Le serveur reste l'autorité : la passerelle refuse toute écriture sans
// le droit, même si l'interface était contournée.
//
// Onglet : ?tab=<clé> (worlds.tabs.ts). Sans ?tab, le dernier onglet visité (mémorisé dans le
// navigateur), sinon l'accueil. Les confirmations passent par ConfirmHost, monté ici une fois.
// Les navigations internes gardent le groupe de jeu de l'URL (?g=, withGameGroup).
//
// Carte commune (NationsGlory : Nova édite, Iris suit) : sous Iris, /state annonce le mode
// « follower » ; la page passe en lecture seule avec le bandeau « Carte commune, éditée depuis
// Nova » et un bouton de bascule. Seule la synchro du roster reste possible : elle vise la
// NGCountryAPI d'Iris, dont la possession et l'état sont affichés.
// ============================================================

function initialTab(): WorldsTabKey {
  const v = loadPref<string | null>(TAB_STORAGE_KEY, null);
  return isWorldsTab(v) ? v : DEFAULT_TAB;
}

export function WorldsPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const setActiveGameGroup = useGameStore((s) => s.setActiveGameGroup);
  const canWrite = useAuthStore((s) => s.canWrite);
  const noWriteRight = !canWrite('config.worlds');
  const gameGroup = useActiveGameGroup();
  const groupsEntry = useGameGroupsStore((s) => s.entries[activeGameId]);
  const [params, setParams] = useSearchParams();
  const urlTab = params.get('tab');
  const [lastTab, setLastTab] = useState<WorldsTabKey>(initialTab);
  const tab: WorldsTabKey = isWorldsTab(urlTab) ? urlTab : lastTab;
  const focusCountry = params.get('pays');

  const state = useWorldsStore((s) => s.state);
  const stateError = useWorldsStore((s) => s.stateError);
  const loading = useWorldsStore((s) => s.loading);
  const level = useWorldsStore((s) => s.level);
  const levels = useWorldsStore((s) => s.levels);
  const levelsVersion = useWorldsStore((s) => s.levelsVersion);
  const init = useWorldsStore((s) => s.init);
  const poll = useWorldsStore((s) => s.poll);
  const reset = useWorldsStore((s) => s.reset);
  const setLevel = useWorldsStore((s) => s.setLevel);
  const stats = useWorldsStats();
  const [importOpen, setImportOpen] = useState(false);

  const gameName = getGameById(activeGameId).name;
  // Suiveur de la carte commune : lecture seule, quel que soit le droit ; la synchro du roster
  // (NGCountryAPI du groupe affiché) reste soumise au seul droit d'écriture.
  const follower = isFollower(state);
  const readOnly = noWriteRight || follower;
  const leader = follower ? leaderGroup(state, groupsEntry) : null;
  const leaderLabel = leader?.label ?? "l'éditeur";
  const groupLabel = gameGroup?.label ?? state?.group ?? 'ce groupe';
  // Éditeur d'une carte suivie par d'autres groupes (Nova, suivie par Iris) : ses écritures valent pour eux.
  const followers = !follower && state && !state.empty ? followersOf(groupsEntry, gameGroup?.key) : [];

  // Dernier onglet visité : rouvert quand l'URL n'en porte pas.
  useEffect(() => {
    setLastTab(tab);
    savePref(TAB_STORAGE_KEY, tab);
  }, [tab]);

  // Remise à zéro au montage et à chaque changement de projet.
  useEffect(() => {
    reset();
    void init();
    return () => reset();
  }, [activeGameId, init, reset]);

  // Versions toutes les 10 s, seulement onglet visible ; rattrapage immédiat au retour sur l'onglet.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') void poll(); };
    const t = window.setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', tick); };
  }, [poll]);

  const go = (key: WorldsTabKey, extra?: Record<string, string>) => {
    const next = new URLSearchParams();
    next.set('tab', key);
    Object.entries(extra ?? {}).forEach(([k, v]) => next.set(k, v));
    setParams(withGameGroup(next, params), { replace: false });
  };

  /** Bascule vers un autre groupe du jeu : la page se remonte et relit ce groupe (clé de l'Outlet). */
  const switchButton = (key: string, label: string) => (
    <button type="button" className={styles.secondaryBtn} onClick={() => setActiveGameGroup(activeGameId, key)}>
      <ArrowRightLeft size={14} aria-hidden /> Passer sur {label}
    </button>
  );
  const switchToLeader = leader && leader.available && leader.key !== gameGroup?.key
    ? switchButton(leader.key, leader.label)
    : null;

  // Compteurs des onglets, affichés une fois la liste chargée.
  const counts: Partial<Record<WorldsTabKey, number>> = {
    pays: stats.countries.loaded ? stats.countries.total : undefined,
    regions: stats.regions.loaded ? stats.regions.total : undefined,
    niveaux: levelsVersion !== -1 ? stats.levels : undefined,
    marqueurs: stats.markers.loaded ? stats.markers.total : undefined,
  };

  const header = (
    <header className={`${styles.header} ${ws.pageHead}`}>
      <div className={ws.headMain}>
        <span className={ws.headIcon} aria-hidden><Globe size={22} /></span>
        <div className={ws.headText}>
          <h1 className={styles.title}>Mondes</h1>
          <p className={styles.subtitle}>
            Carte des pays par chunk, drapeaux, géo-régions, niveaux et marqueurs de biomes de {gameName}.
            Le roster fondable en jeu (NGCountryAPI) suit les pays d'ici.
          </p>
        </div>
      </div>
      {state && !state.empty && (
        <div className={ws.headActions}>
          {levels.length > 0 && (
            <select className={`${styles.input} ${ws.levelSelect}`} value={level ?? ''} title="Niveau affiché"
              aria-label="Niveau affiché" onChange={(e) => void setLevel(e.target.value)}>
              {levels.map((l) => <option key={l.name} value={l.name}>{l.title} ({l.name}) · {fmtInt(l.chunkCount)} chunks</option>)}
            </select>
          )}
          <button type="button" className={styles.secondaryBtn} onClick={() => void init()} disabled={loading}>
            {loading ? <Loader2 size={14} className={ws.spinning} /> : <RefreshCw size={14} />} Rafraîchir
          </button>
          {!readOnly && (
            <button type="button" className={styles.secondaryBtn} onClick={() => setImportOpen(true)}>
              <DatabaseZap size={14} /> Import NG Java
            </button>
          )}
        </div>
      )}
    </header>
  );

  let body: React.ReactNode;
  if (stateError && worldsMissingOn(groupsEntry, gameGroup?.key)) {
    // Satellite du groupe sans module worlds : le dire, plutôt que le 404 brut du module.
    const other = groupsEntry?.groups.find((g) => g.key !== gameGroup?.key && g.available && g.capabilities.includes('worlds'));
    body = (
      <section className={styles.detail}>
        <EmptyState icon={Globe} title={`Mondes n'est pas activé sur ${groupLabel}`}
          text={(
            <p>
              Le satellite de {groupLabel} n'annonce pas le module « worlds ».
              {other ? ` La carte se consulte sur ${other.label}.` : ''}
            </p>
          )}
          actions={other ? switchButton(other.key, other.label) : undefined} />
      </section>
    );
  } else if (stateError) {
    body = (
      <section className={styles.detail}>
        <EmptyState icon={TriangleAlert} tone="bad" title="Module Mondes indisponible"
          text={(
            <>
              <p>{stateError}</p>
              <p>
                Le module « worlds » est servi par le satellite de {gameName} (version 1.0.3 ou plus du
                satellite NationsGlory). Réessaie une fois le satellite à jour.
              </p>
            </>
          )}
          actions={(
            <button type="button" className={styles.secondaryBtn} onClick={() => void init()} disabled={loading}>
              {loading ? <Loader2 size={14} className={ws.spinning} /> : <RefreshCw size={14} />} Réessayer
            </button>
          )} />
      </section>
    );
  } else if (!state) {
    body = (
      <section className={styles.detail}>
        <EmptyState icon={Loader2} spin title="Chargement des mondes…" text="État du module, niveaux, pays et grille du niveau principal." />
      </section>
    );
  } else if (state.empty && follower) {
    body = (
      <section className={styles.detail}>
        <EmptyState icon={Share2} title="Carte commune encore vide"
          text={(
            <>
              <p>
                {groupLabel} suit la carte de {leaderLabel}, qui n'a encore ni pays ni niveau : l'import NG Java
                se fait depuis {leaderLabel}.
              </p>
              <p>
                Si la carte de {leaderLabel} est déjà remplie, la datasource de modules.worlds du satellite de
                {' '}{groupLabel} ne vise pas la même base que celle de {leaderLabel}.
              </p>
            </>
          )}
          actions={switchToLeader ?? undefined} />
      </section>
    );
  } else if (state.empty) {
    body = (
      <section className={styles.detail}>
        <ImportPanel readOnly={readOnly} onImported={() => void init()} />
        {readOnly && <p className={styles.muted}>Lecture seule : l'import demande le droit d'écriture sur cette page.</p>}
      </section>
    );
  } else {
    const needsLevel = !level && (tab === 'carte' || tab === 'marqueurs');
    body = (
      <>
        <nav className={`${styles.tabBar} ${ws.tabBarScroll}`} role="tablist" aria-label="Sections de la page Mondes">
          {WORLDS_TABS.map((t) => {
            const Icon = t.icon;
            const on = tab === t.key;
            const n = counts[t.key];
            return (
              <button key={t.key} type="button" role="tab" aria-selected={on}
                className={`${styles.tabBtn} ${on ? styles.tabBtnOn : ''}`} onClick={() => go(t.key)}>
                <Icon size={16} className={ws.tabIcon} aria-hidden />
                {t.label}
                {n != null && <span className={ws.tabCount}>{fmtInt(n)}</span>}
              </button>
            );
          })}
          {readOnly && (
            <span className={ws.roNote}>
              {follower
                ? <><Share2 size={12} aria-hidden /> Carte commune : lecture seule</>
                : <><Lock size={12} /> Lecture seule</>}
            </span>
          )}
        </nav>
        {needsLevel ? (
          <section className={styles.detail}>
            <EmptyState icon={Globe} title="Aucun niveau"
              text={readOnly
                ? 'La carte s\'affiche sur un niveau (monde Minecraft). Aucun n\'est déclaré pour l\'instant.'
                : 'La carte s\'affiche sur un niveau (monde Minecraft). Crée le premier, par exemple overworld, pour peindre les pays et placer les marqueurs.'}
              actions={readOnly ? (
                <button type="button" className={styles.secondaryBtn} onClick={() => go('niveaux')}>
                  <Globe size={14} /> Voir les niveaux
                </button>
              ) : (
                <button type="button" className={styles.primaryBtn} onClick={() => go('niveaux', { nouveau: '1' })}>
                  <Plus size={14} /> Créer un niveau
                </button>
              )} />
          </section>
        ) : (
          <>
            {tab === 'accueil' && <HomeTab readOnly={readOnly} onGo={go} />}
            {tab === 'carte' && <MapTab readOnly={readOnly} focusCountryId={focusCountry} />}
            {tab === 'pays' && (
              <CountriesTab readOnly={readOnly} syncReadOnly={noWriteRight}
                onShowOnMap={(id) => go('carte', { pays: id })} />
            )}
            {tab === 'regions' && <GeoRegionsTab readOnly={readOnly} />}
            {tab === 'niveaux' && <LevelsTab readOnly={readOnly} />}
            {tab === 'marqueurs' && <MarkersTab readOnly={readOnly} />}
          </>
        )}
      </>
    );
  }

  // Bandeau de la carte commune, au-dessus de tous les onglets : lecture seule sur le suiveur (Iris),
  // rappel repliable sur l'éditeur (Nova) que ses modifications valent aussi pour les suiveurs.
  const followerLabels = followers.map((g) => g.label).join(', ');
  let sharedBanner: React.ReactNode = null;
  if (follower) {
    sharedBanner = (
      <div className={ws.sharedBanner}>
        <InfoBox icon={Share2} title={`Carte commune, éditée depuis ${leaderLabel}`}>
          <p className={ws.sharedBannerText}>
            {groupLabel} et {leaderLabel} partagent la carte et les pays fondables : pour les modifier, passe
            sur {leaderLabel}. Ici, tout est en lecture seule. La possession (qui a fondé quoi) et la synchro du
            roster affichées sont celles de {groupLabel}.
          </p>
          {switchToLeader && <div className={ws.sharedBannerActions}>{switchToLeader}</div>}
        </InfoBox>
      </div>
    );
  } else if (followers.length > 0) {
    sharedBanner = (
      <div className={ws.sharedBanner}>
        <InfoBox icon={Share2} storageKey="worlds.sharedMap.note"
          title={`Carte commune à ${groupLabel} et ${followerLabels}`}>
          <p className={ws.sharedBannerText}>
            Cette carte est éditée ici, sur {groupLabel}, et suivie par {followerLabels} : un pays créé, renommé
            ou supprimé l'est aussi pour {followerLabels}, dont le roster en jeu suit. La possession reste propre à
            chaque groupe.
          </p>
        </InfoBox>
      </div>
    );
  }

  return (
    <div className={`${styles.page} ${ws.root}`}>
      {header}
      {sharedBanner}
      {body}
      {importOpen && (
        <Modal title="Import NG Java" icon={<DatabaseZap size={16} />} size="wide" onClose={() => setImportOpen(false)}
          subtitle="Ressource NG Java embarquée dans le satellite : vérification, import ou remplacement.">
          <ImportPanel inModal readOnly={readOnly} onImported={() => void init()} />
        </Modal>
      )}
      <ConfirmHost />
    </div>
  );
}
